<template>
  <div class="search" :class="{ 'search--open': open }">
    <form class="form-inline" method="GET" :action="searchPage">
      <button type="button" tabindex="0" class="search__close" @click="toggle(false)">
        <svg class="bi" width="22" height="22" fill="currentColor">
          <use href="../static/bi-sprite.svg?#arrow-left-2" />
        </svg>
      </button>
      <div class="search__field" @focusin="onFocusIn">
        <search-field @input="onSearchTermUpdated" :delay="isSearchPage ? 0 : 300" />
      </div>
    </form>
    <div class="search__overlay" v-if="open">
      <div class="search__row">
        <template v-if="noResults">
          <div class="col-12 text-center pt-4">
            <p class="search-results__count">
              0 resultater for
            </p>
            <p class="search-results__term" v-if="searchTerm">“{{ searchTerm }}”</p>
          </div>
        </template>
        <template v-else-if="!termNotReadyForSearch">
          <div class="col-12 col-lg-3 search__sidebar">
            <div class="navigation__label mb-3">Kategorier</div>
            <link-list :list="categories" :search-term="searchTerm" v-if="categories && categories.length > 0"></link-list>
            <p v-else>-</p>
            <template v-if="products && products.length > 0">
              <div class="navigation__label pt-2 pt-md-0 mt-4 mb-3">Sider</div>
              <link-list :list="pages" :search-term="searchTerm" v-if="pages && pages.length > 0"></link-list>
              <p v-else>-</p>
            </template>
          </div>
          <div class="col-12 col-lg-9 search__products">
            <template v-if="products && products.length > 0">
              <div class="search__scroll-btn-left" aria-hidden="true" @click.prevent="scrollLeft" :class="{'disabled': !showLeftScrollButton}">
                <svg width="18" height="18" fill="currentColor" class="bi"><use href="../static/bi-sprite.svg?#chevron-left"></use></svg>
              </div>
              <div class="search__scroll-btn-right" aria-hidden="true" @click.prevent="scrollRight" :class="{'disabled': !showRightScrollButton}">
                <svg width="18" height="18" fill="currentColor" class="bi"><use href="../static/bi-sprite.svg?#chevron-right"></use></svg>
              </div>
              <div class="search__products-header">
                <span class="navigation__label m-0">Produkter</span>
                <a class="btn btn-cta" :href="`${searchPage}?q=${searchTerm}`">Se alle</a>
              </div>
              <div class="row products my-0" v-on="scrollEndEventSupported ? null : { scroll: (e) => onScroll(e) }" @scrollend="onScrollEnd" ref="products">
                <div v-for="product in products" class="col-6 col-lg-6 col-xl-4 px-0">
                  <Tile :product="product.productData" :settings="{currency: settings.currency}" :collection-mode="false" :dict="dict">
                  </Tile>
                </div>
              </div>
            </template>
            <div v-else-if="pages && pages.length > 0" class="ml-4">
              <div class="navigation__label mb-3">Sider</div>
              <link-list :list="pages" :search-term="searchTerm"></link-list>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="col-12 col-lg-3 search__sidebar">
            <div class="navigation__label mb-3 mb-lg-4 pb-lg-1">Populære Søgninger</div>
            <link-list :list="popularSearches"></link-list>
          </div>
          <div class="col-12 col-lg-9 search__categories pl-lg-4">
            <div class="navigation__label mb-3">Populære Kategorier</div>
            <link-list :list="popularCategories" type="button"></link-list>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import SearchField from "./components/SearchField.vue";
import LinkList from "./components/LinkList.vue";
import Tile from "./components/Tile.vue";
import axios from 'axios';

export default Vue.extend({
  name: "Search",
  components: {
    SearchField,
    LinkList,
    Tile,
  },
  props: [],
  data() {
    return {
      open: false,
      loading: false,
      searchTerm: "",
      categories: [],
      pages: [],
      products: [],
      settings: null,
      dict: [],
      instantSearchOnTerm: ["is", "si", "te"],
      searchPage: null,
      popularSearches: null,
      popularCategories: null,
      isSearchPage: window.isSearchPage,
      showLeftScrollButton: false,
      showRightScrollButton: false,
      isLargeDesktop: window.matchMedia('(min-width: 1200px)').matches,
      scrollEndEventSupported: 'onscrollend' in window
    };
  },
  created() {
    window.addEventListener("header-toggle", this.onHeaderToggle);
  },
  mounted() {
    var ref = this;
    
    if (!this.isSearchPage) {
      window.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;

        ref.toggle(false);
        if (document.activeElement.id === "search") {
          event.preventDefault()
          document.activeElement.blur();
        }
      });

      axios
        .get(`/umbraco/api/searchapi/productsdictionary?contextid=${window.umbracoContext?.id}`)
        .then((response) => {
          this.dict = response.data.dictionary;
        })
        .catch((error) => {
          console.error(error);        
        });
      
      axios
        .get(`/api/init-search?id=${window.umbracoContext?.id}`)
        .then((response) => {  
          const data = response.data;       
          this.searchPage = data.searchPageUrl;
          this.popularSearches = data.popularSearches;
          this.popularCategories = data.popularCategories;
        })
        .catch((error) => {
          console.error(error);        
        });
      }
  },
  methods: {
    toggle(open) {
      if (this.isSearchPage) {
        return;
      };

      if (open) {
        document.addEventListener('click', this.onDocumentClicked);
      } else {
        document.removeEventListener('click', this.onDocumentClicked);
      }

      var event = new CustomEvent("header-toggle", {
        detail: { search: open, navigation: false }
      });
      window.dispatchEvent(event);
    },
    onDocumentClicked(e) {
      if (!document.querySelector(".search").contains(e.target)){
        this.toggle(false)
      }
    },
    onSearchTermUpdated(term) {
      this.searchTerm = term;

      if (this.termNotReadyForSearch) {
        this.categories = [];
        this.pages = [];
        this.products = [];

        return;
      };

      this.loading = true;

      axios
        .get(`/api/search?query=${this.searchTerm}&hitsperpage=6`)
        .then((resp) => {
          const data = resp.data;
          const hits = data?.hits;
          if (!data || !hits) return;

          this.products = hits.products;
          this.settings = data.settings;
          this.categories = hits.categories;
          this.pages = hits.pages;

          this.loading = false;

          // Safari needs next tick to ensure ref is available
          if(this.refs?.products){
            this.$nextTick(() => {
              this.$refs.products.scrollTo({
                left: 0,
                behavior: 'smooth'
              });
            });
          }
        })
        .catch((error) => {
          console.error(error);
        })
    },
    onHeaderToggle(event) {      
      if (this.isSearchPage) {
        return;
      };
      
      this.open = event.detail.search;
    },
    onFocusIn() {
      if (this.isSearchPage) {
        return;
      };

      this.toggle(true);
    },
    onScroll(e) {
      clearTimeout(window.scrollEndTimer)
      window.scrollEndTimer = setTimeout(() => this.onScrollEnd(e), 100)
    },
    onScrollEnd(e){
      const _hideLeftScrollButton = e.target.scrollLeft < 10;
      const _hideRightScrollButton = e.target.scrollWidth - e.target.scrollLeft < (e.target.clientWidth + 10);
      const _showButtons = this.isLargeDesktop ? this.products.length > 3 : this.products.length > 2;
      this.showRightScrollButton = !_hideRightScrollButton && _showButtons;
      this.showLeftScrollButton = !_hideLeftScrollButton && _showButtons;
      e.preventDefault();
    },
    scrollRight () {
      this.$refs.products.scrollTo({
        left: this.$refs.products.scrollLeft + 200,
      behavior: 'smooth'
      });
    },
    scrollLeft () {
      this.$refs.products.scrollTo({
        left: this.$refs.products.scrollLeft - 200,
        behavior: 'smooth'
      });
    }
  },
  computed: {
    termNotReadyForSearch() {
      return this.searchTerm.length < 3 && !this.instantSearchOnTerm.includes(this.searchTerm.toLowerCase());
    },
    noResults() {      
      return !this.products && !this.categories && !this.pages;
    }
  },
  watch: {}
});
</script>
